import { useMemo, useState } from 'react';
import { DiffEditor } from '@monaco-editor/react';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import CheckIcon from '@material-ui/icons/Check';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import DescriptionIcon from '@material-ui/icons/Description';

import type { DraftOperation } from '../../api';
import { useKnowledgeBuilder } from './KnowledgeBuilderProvider';
import { languageForPath } from './MonacoDocumentEditor';
import { MarkdownPreview } from './MarkdownPreview';
import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';
import {
  buildPreviewDocument,
  previewForOperation,
} from './proposedChangePreview';

const EXPANDED_DRAFT_WIDTH = 260;
const COLLAPSED_DRAFT_WIDTH = 44;

type ReviewMode = 'preview' | 'diff';

export const ProposedChangeReview = () => {
  const {
    approveProposedChange,
    documents,
    proposedChanges,
    rejectProposedChange,
  } = useKnowledgeBuilder();

  const colors = useKnowledgeBuilderColors();

  const pending = proposedChanges.filter(
    change => change.status === 'pending',
  );

  const [selectedId, setSelectedId] = useState<string>();

  const [selectedOperationIndex, setSelectedOperationIndex] =
    useState<number>();

  const [draftPanelOpen, setDraftPanelOpen] = useState(true);

  const [reviewModeOverride, setReviewModeOverride] =
    useState<ReviewMode>();

  const selected =
    pending.find(change => change.id === selectedId) ?? pending[0];

  const pendingOperationIndexes = useMemo(
    () =>
      selected
        ? selected.operations
            .map((_operation, index) => index)
            .filter(
              index =>
                operationStatus(selected.operations[index]) === 'pending',
            )
        : [],
    [selected],
  );

  const effectiveOperationIndex =
    selectedOperationIndex !== undefined &&
    pendingOperationIndexes.includes(selectedOperationIndex)
      ? selectedOperationIndex
      : pendingOperationIndexes[0];

  const selectedOperation =
    effectiveOperationIndex !== undefined
      ? selected?.operations[effectiveOperationIndex]
      : undefined;

  const preview = useMemo(
    () => previewForOperation(selectedOperation, documents),
    [documents, selectedOperation],
  );

  const defaultReviewMode = reviewModeForPath(preview.path);

  const reviewMode =
    reviewModeOverride ?? defaultReviewMode;

  const previewDocument = useMemo(
    () => buildPreviewDocument(preview, documents),
    [documents, preview],
  );

  if (!pending.length) {
    return (
      <Typography
        style={{
          padding: 24,
          color: colors.text.muted,
        }}
      >
        No pending AI changes.
      </Typography>
    );
  }

  const selectOperation = (
    changeId: string,
    operationIndex: number,
  ) => {
    setSelectedId(changeId);
    setSelectedOperationIndex(operationIndex);
    setReviewModeOverride(undefined);
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        className="knowledge-builder-scrollbar"
        style={{
          width: draftPanelOpen
            ? EXPANDED_DRAFT_WIDTH
            : COLLAPSED_DRAFT_WIDTH,

          flex: `0 0 ${
            draftPanelOpen
              ? EXPANDED_DRAFT_WIDTH
              : COLLAPSED_DRAFT_WIDTH
          }px`,

          borderRight: `1px solid ${colors.border.default}`,

          background: colors.bg.panel,

          overflowX: 'hidden',

          overflowY: draftPanelOpen
            ? 'auto'
            : 'hidden',

          transition:
            'width 180ms ease, flex-basis 180ms ease',
        }}
      >
        <div
          style={{
            height: 42,

            display: 'flex',
            alignItems: 'center',

            justifyContent: draftPanelOpen
              ? 'space-between'
              : 'center',

            padding: draftPanelOpen
              ? '0 6px 0 12px'
              : 0,

            borderBottom: `1px solid ${colors.border.default}`,

            boxSizing: 'border-box',
          }}
        >
          {draftPanelOpen && (
            <Typography
              variant="caption"
              style={{
                color: colors.text.muted,

                textTransform: 'uppercase',

                letterSpacing: 0.4,
              }}
            >
              Draft changes
            </Typography>
          )}

          <Tooltip
            title={
              draftPanelOpen
                ? 'Collapse draft list'
                : 'Expand draft list'
            }
          >
            <IconButton
              size="small"
              onClick={() =>
                setDraftPanelOpen(
                  current => !current,
                )
              }
            >
              {draftPanelOpen ? (
                <ChevronLeftIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </div>

        {draftPanelOpen ? (
          pending.map(change => (
            <div key={change.id}>
              <div
                style={{
                  padding: '10px 12px 4px',
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: colors.text.muted,

                    textTransform: 'uppercase',

                    letterSpacing: 0.4,
                  }}
                >
                  draft v
                  {change.expectedDraftVersion}
                </Typography>
              </div>

              {change.operations.map(
                (operation, index) => {
                  if (
                    operationStatus(operation) !==
                    'pending'
                  ) {
                    return null;
                  }

                  const isSelected =
                    change.id === selected?.id &&
                    index ===
                      effectiveOperationIndex;

                  return (
                    <button
                      key={`${change.id}-${index}`}
                      type="button"
                      onClick={() =>
                        selectOperation(
                          change.id,
                          index,
                        )
                      }
                      style={{
                        display: 'block',

                        width: '100%',

                        padding: '8px 12px',

                        border: 0,

                        borderBottom: `1px solid ${colors.border.default}`,

                        background: isSelected
                          ? colors.bg.hover
                          : colors.bg.panel,

                        color:
                          colors.text.primary,

                        textAlign: 'left',

                        cursor: 'pointer',

                        fontSize: 12,

                        wordBreak: 'break-word',
                      }}
                    >
                      {operationLabel(
                        operation,
                      )}
                    </button>
                  );
                },
              )}
            </div>
          ))
        ) : (
          <div
            style={{
              display: 'flex',

              justifyContent: 'center',

              paddingTop: 10,
            }}
          >
            <Tooltip
              title={`${pending.length} pending draft${
                pending.length === 1
                  ? ''
                  : 's'
              }`}
            >
              <DescriptionIcon
                fontSize="small"
                style={{
                  color:
                    colors.text.muted,
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div
        style={{
          flex: 1,

          minWidth: 0,

          display: 'flex',

          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',

            alignItems: 'center',

            justifyContent: 'space-between',

            gap: 12,

            padding: '10px 16px',

            borderBottom: `1px solid ${colors.border.default}`,

            background: colors.bg.panel,
          }}
        >
          <div
            className="knowledge-builder-scrollbar"
            style={{
              minWidth: 0,

              maxHeight: 220,

              overflowY: 'auto',
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                color: colors.text.primary,
              }}
            >
              {preview.path ??
                'Proposed change'}
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: colors.text.muted,
              }}
            >
              {selectedOperation?.explanation ||
                selected?.message}
            </Typography>
          </div>

          <div
            style={{
              display: 'flex',

              alignItems: 'center',

              gap: 8,

              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',

                alignItems: 'center',

                border: `1px solid ${colors.border.default}`,

                borderRadius: 5,

                overflow: 'hidden',
              }}
            >
              <Button
                type="button"
                size="small"
                startIcon={
                  <DescriptionIcon />
                }
                onClick={() =>
                  setReviewModeOverride(
                    'preview',
                  )
                }
                disabled={!previewDocument}
                style={{
                  minWidth: 96,

                  borderRadius: 0,

                  background:
                    reviewMode ===
                    'preview'
                      ? colors.bg.hover
                      : 'transparent',

                  color:
                    reviewMode ===
                    'preview'
                      ? colors.accent
                          .blue
                      : colors.text
                          .primary,
                }}
              >
                Preview
              </Button>

              <Button
                type="button"
                size="small"
                startIcon={
                  <CompareArrowsIcon />
                }
                onClick={() =>
                  setReviewModeOverride(
                    'diff',
                  )
                }
                style={{
                  minWidth: 82,

                  borderRadius: 0,

                  borderLeft: `1px solid ${colors.border.default}`,

                  background:
                    reviewMode === 'diff'
                      ? colors.bg.hover
                      : 'transparent',

                  color:
                    reviewMode === 'diff'
                      ? colors.accent
                          .blue
                      : colors.text
                          .primary,
                }}
              >
                Diff
              </Button>
            </div>

            {selected &&
              effectiveOperationIndex !==
                undefined && (
                <>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    startIcon={
                      <CloseIcon />
                    }
                    onClick={() =>
                      rejectProposedChange(
                        selected.id,
                        effectiveOperationIndex,
                      )
                    }
                    style={{
                      borderColor:
                        colors.border
                          .strong,

                      color:
                        colors.text
                          .primary,
                    }}
                  >
                    Reject
                  </Button>

                  <Button
                    type="button"
                    size="small"
                    color="primary"
                    variant="contained"
                    startIcon={
                      <CheckIcon />
                    }
                    onClick={() =>
                      approveProposedChange(
                        selected.id,
                        effectiveOperationIndex,
                      )
                    }
                  >
                    Approve
                  </Button>
                </>
              )}
          </div>
        </div>

        <div
          style={{
            flex: 1,

            minHeight: 0,

            overflow: 'hidden',
          }}
        >
          {reviewMode === 'preview' &&
          previewDocument ? (
            <MarkdownPreview
              document={previewDocument}
            />
          ) : (
            <>
              {preview.original ===
                preview.modified && (
                <div
                  style={{
                    padding:
                      '8px 12px',

                    background:
                      colors.status
                        .warningBg,

                    color:
                      colors.status
                        .warningText,

                    borderBottom: `1px solid ${colors.status.warningBorder}`,

                    fontSize: 12,
                  }}
                >
                  No textual diff was
                  produced for this
                  operation.
                </div>
              )}

              <DiffEditor
                original={
                  preview.original
                }
                modified={
                  preview.modified
                }
                language={languageForPath(
                  preview.path ??
                    'proposal.txt',
                )}
                theme={
                  colors.monacoTheme
                }
                keepCurrentOriginalModel
                keepCurrentModifiedModel
                options={{
                  readOnly: true,

                  minimap: {
                    enabled: true,
                    scale: 1,
                    showSlider:
                      'mouseover',
                  },

                  scrollBeyondLastLine:
                    false,

                  automaticLayout: true,

                  lineNumbersMinChars: 4,

                  padding: {
                    top: 12,
                    bottom: 12,
                  },
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function reviewModeForPath(
  path?: string,
): ReviewMode {
  if (!path) {
    return 'diff';
  }

  if (
    /\.(md|mdx|png|svg|pdf|docx?)$/i.test(
      path,
    )
  ) {
    return 'preview';
  }

  return 'diff';
}

function operationStatus(
  operation: DraftOperation,
) {
  return (
    operation.status ??
    'pending'
  );
}

function operationLabel(
  operation: DraftOperation,
) {
  if (
    operation.type ===
    'renameFile'
  ) {
    return `${operation.type}: ${operation.from} -> ${operation.to}`;
  }

  return `${operation.type}: ${operation.path}`;
}
